import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, KeyRound, Trash2, Loader2 } from "lucide-react";

const ROLE_BADGE = {
  owner: "bg-primary/15 text-primary border-primary/30",
  manager: "bg-secondary/15 text-secondary border-secondary/30",
  captain: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  client: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

function CreateUserDialog({ isOwner }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "captain", password: "" });
  const roles = isOwner ? ["manager", "captain", "client"] : ["captain", "client"];

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post("/users", payload)).data,
    onSuccess: () => {
      toast.success("User created");
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["captains"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      setOpen(false);
      setForm({ name: "", email: "", role: "captain", password: "" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-user-button"><UserPlus className="h-4 w-4 mr-2" /> Add User</Button>
      </DialogTrigger>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create account</DialogTitle>
          <DialogDescription>Set the user's role and a login password. They can sign in immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form); }} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1.5" data-testid="user-name-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required className="mt-1.5" data-testid="user-email-input" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Password</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required placeholder="Set a login password" className="mt-1.5" data-testid="user-password-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="submit-user-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ targetUser }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/users/${targetUser.id}/reset-password`, { password })).data,
    onSuccess: () => { toast.success(`Password updated for ${targetUser.name}`); setOpen(false); setPassword(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`reset-password-${targetUser.id}`}><KeyRound className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Reset password · {targetUser.name}</DialogTitle>
          <DialogDescription>Enter a new password. The user will use it on their next sign in.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <Label>New password</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="confirm-reset-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user } = useOutletContext();
  const qc = useQueryClient();
  const isOwner = user.role === "owner";
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const del = useMutation({
    mutationFn: async (uid) => (await api.delete(`/users/${uid}`)).data,
    onSuccess: () => { toast.success("User deleted"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow text-primary">Administration</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">User Management</h1>
        </div>
        <CreateUserDialog isOwner={isOwner} />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden sm:table-cell">Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {users.map((u) => {
                const canReset = isOwner || !["owner", "manager"].includes(u.role);
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full border text-xs font-mono-tag uppercase ${ROLE_BADGE[u.role]}`}>
                        {ROLE_LABELS[u.role]}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canReset && <ResetPasswordDialog targetUser={u} />}
                        {isOwner && u.id !== user.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="delete-user-dialog">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                                <AlertDialogDescription>This permanently removes the {ROLE_LABELS[u.role]} account.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del.mutate(u.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-user-${u.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
